import { Component, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { getProgramId, getSelectedLocation, getStaffInfo } from "../shared/utils/storage-utils";
import { DeliveryService } from "../core/services/delivery.service";
import { getImageUrl } from "../shared/utils/commenFunctions";

type DeliveryServiceCard = {
  id: string;
  label: string;
  /** Empty when the activity was created without an image. */
  image: string;
  alt: string;
  route: string[];
  activityId?: string;
  kind: 'meal' | 'transport' | 'activity';
};

@Component({
  selector: "staff-delivery-home-component, StaffDeliveryHomeComponent",
  standalone: true,
  templateUrl: './staff-delivery-home.component.html',
  styleUrls: ['./staff-delivery-home.component.css'],
  imports: [CommonModule, LibraryModule]
})
export class StaffDeliveryHomeComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly deliveryService = inject(DeliveryService);

  staffName = '';
  isLoadingActivities = false;
  serviceCards: DeliveryServiceCard[] = [];

  private readonly staticCards: DeliveryServiceCard[] = [
    {
      id: 'meal',
      kind: 'meal',
      label: 'Meal',
      image: 'assets/images/IMG00056.png',
      alt: 'Meal service',
      route: ['/staff-delivery-layout/check-in'],
    },
    {
      id: 'transport',
      kind: 'transport',
      label: 'Transportation',
      image: 'assets/images/IMG00004.png',
      alt: 'Transportation service',
      route: ['/staff-delivery-layout/transport'],
    },
  ];

  ngOnInit(): void {
    this.staffName = getStaffInfo()?.staffName?.trim() || 'Staff';
    this.serviceCards = [...this.staticCards];
    this.loadActivityCards();
  }

  goBack(): void {
    void this.router.navigate(['/programs/senior-center']);
  }

  onSelectService(card: DeliveryServiceCard): void {
    if (card.kind === 'activity' && card.activityId) {
      this.router.navigate(card.route, {
        state: { activityName: card.label }
      });
      return;
    }
    this.router.navigate(card.route);
  }

  private loadActivityCards(): void {
    const programId = getProgramId();
    if (!programId) {
      return;
    }

    this.isLoadingActivities = true;
    const location = getSelectedLocation();
    this.deliveryService.getActivityDeliveryOptions({
      programId,
      ...(location ? { locationId: location.id } : {})
    }).subscribe({
      next: (response) => {
        const activities = response?.statusCode === 200 ? (response.activities ?? []) : [];
        const activityCards: DeliveryServiceCard[] = activities.map((activity) => {
          const rawImage = (activity.activityImage ?? '').trim();
          return {
            id: `activity-${activity.activityId}`,
            kind: 'activity' as const,
            activityId: activity.activityId,
            label: activity.activityName?.trim() || 'Activity',
            // Only show the activity's own image — no generic Activity fallback.
            image: rawImage ? getImageUrl(rawImage) : '',
            alt: `${activity.activityName || 'Activity'} service`,
            route: ['/staff-delivery-layout/activity', activity.activityId],
          };
        });
        this.serviceCards = [...this.staticCards, ...activityCards];
        this.isLoadingActivities = false;
      },
      error: () => {
        this.serviceCards = [...this.staticCards];
        this.isLoadingActivities = false;
      }
    });
  }
}
